import { useEffect, useState } from "react";
import {
  BpmSettings,
  buildDefaultBpmSettings,
  buildEffect,
  buildSwapEffect,
  EffectSetting,
  EffectTitle,
  MAX_EFFECTS,
} from "@/components/pages/beats/data/beats-effects.data";
import { useLoggingContext } from "@/providers/logging-provider/logging-provider";
import { useDownload } from "@/hooks/use-download";
import { ErrorResponse } from "@/data/response.data";
import { useGlobalModalContext } from "@/providers/modal-provider/modal-provider";
import { useCreateProcessInputsHandler } from "@/hooks/ai-tool-process/use-create-process-inputs-handler";
import { useAnonServiceChecker } from "@/hooks/use-anon-service-checker";
import { beatMachineUsageStorage } from "@/services/storage/beat-machine-session-storage";
import { beatMachineLicenseManager } from "@/services/license/license-managers/beat-machine-license-manager";

export const useBeats = () => {
  const logger = useLoggingContext();

  const { downloadLocal } = useDownload();

  const { showModal } = useGlobalModalContext();

  const [bpmSettings, setBpmSettings] = useState<BpmSettings>(
    buildDefaultBpmSettings(),
  );

  const [isLoading, setIsLoading] = useState<boolean>();

  const { songFile } = useCreateProcessInputsHandler();

  const [resultFile, setResultFile] = useState<File | undefined>();

  const [canStartApplyingEffects, setCanStartApplyingEffects] = useState(false);

  const { validate } = useAnonServiceChecker({
    authTrialUserUsageCount: 4,
    anonUserUsageCount: 2,
    serviceName: "Beat Machine",
    serviceStorage: beatMachineUsageStorage,
    licenseValidator:
      beatMachineLicenseManager.downloadResultLimiter.isDownloadEnabled,
  });

  const downloadResultFile = async () => {
    if (!resultFile) {
      throw new Error("Result file is missing");
    }

    await downloadLocal(URL.createObjectURL(resultFile), resultFile.name);
  };

  const setIsManualTempo = (value: boolean) => {
    setBpmSettings({ ...buildDefaultBpmSettings(), isManualTempo: value });
  };

  const setBpm = (value: number) => {
    const isValid = validateBpmSetting(value);

    setBpmSettings((prev) => ({
      ...prev,
      bpm: value,
      isBpmValid: isValid,
      bpmValidationMessage: "Bpm must be greater than 0",
    }));
  };

  const setTolerance = (value: number) => {
    const isValid = validateBpmSetting(value);

    setBpmSettings((prev) => ({
      ...prev,
      tolerance: value,
      isToleranceValid: isValid,
      toleranceValidationMessage: "Tolerance must be greater than 0",
    }));
  };

  const validateBpmSetting = (value: number) => {
    return value > 0;
  };

  const [effects, setEffects] = useState<EffectSetting[]>([buildSwapEffect()]);

  const canAddNewEffect = effects.length < MAX_EFFECTS;
  const canRemoveLastEffect = effects.length > 1;

  useEffect(() => {
    const isEveryEffectValid = effects.every((effect) => effect.isValid);

    setCanStartApplyingEffects(
      bpmSettings.isBpmValid &&
        bpmSettings.isToleranceValid &&
        isEveryEffectValid &&
        songFile !== null,
    );
  }, [bpmSettings, songFile, effects]);

  const addNewEffect = () => {
    setEffects((prev) => [...prev, buildSwapEffect()]);
  };

  const removeEffect = (index: number) => {
    setEffects((prev) => [...prev.filter((item, i) => i !== index)]);
  };

  const changeEffectType = (index: number, effectType: EffectTitle) => {
    const newEffect = buildEffect(effectType);

    setEffects((prev) => [
      ...prev.map((item, i) => (i === index ? newEffect : item)),
    ]);
  };

  const updateEffectParameterValue = (
    index: number,
    parameterTitle: string,
    value: number,
  ) => {
    setEffects((prev) => {
      const effectToUpdate = {
        ...prev[index],
        parameters: [
          ...prev[index].parameters.map((param) =>
            param.title !== parameterTitle ? param : { ...param, value: value },
          ),
        ],
      };

      const validationResult = effectToUpdate.validate();

      effectToUpdate.isValid = validationResult.isSuccess;
      effectToUpdate.errorMessages = validationResult.errorMessages;

      return [
        ...prev.map((effect, i) => (i === index ? effectToUpdate : effect)),
      ];
    });
  };

  const applyEffects = async () => {
    setIsLoading(true);
    if (!(await validate())) {
      setIsLoading(false);
      return;
    }

    try {
      const form = new FormData();
      form.append("file", songFile!);
      form.append("bpm", bpmSettings.bpm.toString());
      form.append("tolerance", bpmSettings.tolerance.toString());
      form.append("effects", JSON.stringify(effects));

      const response = await fetch("/api/beats", {
        method: "POST",
        body: form,
      });

      if (response.ok) {
        const blob = await response.blob();
        setResultFile(new File([blob], songFile!.name, { type: blob.type }));
      } else {
        const json = await response.json();
        const errorResponse = JSON.parse(json) as ErrorResponse;

        logger.logError(new Error(errorResponse.message));

        showErrorModal(errorResponse.message);
      }
    } catch (e) {
      const error = e as Error;
      logger.logError(error);
      showErrorModal(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const showErrorModal = (message: string) => {
    showModal({
      type: "error_modal",
      props: {
        title: "Failed to apply beat effects",
        message: message,
      },
    });
  };

  return {
    isLoading,
    resultFile,
    downloadResultFile,
    bpmSettings,
    setIsManualTempo,
    setBpm,
    setTolerance,
    effects,
    canAddNewEffect,
    canRemoveLastEffect,
    canStartApplyingEffects,
    addNewEffect,
    removeEffect,
    changeEffectType,
    updateEffectParameterValue,
    applyEffects,
  };
};
